'use client'

/**
 * Las acciones del cuadernillo: descargar el PDF y, como alternativa, imprimir
 * desde el navegador.
 *
 * **El camino principal es un fichero, no `window.print()`.** Aunque imprimir
 * funcione —la causa de las quince páginas está arreglada en `printElement`—,
 * hay algo que ningún CSS puede quitar: la cabecera y el pie que el navegador
 * añade por su cuenta (URL y fecha). Por eso se sirve un PDF generado aparte:
 * nueve páginas exactas, sin diálogo y sin sorpresas.
 *
 * La impresión se queda como secundaria: a quien va a imprimir directamente le
 * ahorra la descarga, y desde el arreglo de `printElement` produce nueve hojas
 * limpias.
 *
 * Existe como client component solo por eso: medir el click y disparar la
 * impresión. Las nueve hojas llegan como `children` desde un padre de servidor,
 * así que no cruzan al bundle.
 */

import { useRef, type ReactNode } from 'react'
import Link from 'next/link'
import { Download, Printer } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { printElement } from '@/lib/guides/tools/print-element'
import { captureProductEvent, PRODUCT_EVENTS } from '@/lib/product-analytics'

const GUIDE_SLUG = 'cuentos-y-actividades-de-halloween-infantil'

export function BookletPrinter({
  children,
  pageCount,
  copy,
  pdf,
  funnel,
}: {
  /** Las hojas, renderizadas en servidor. */
  children: ReactNode
  pageCount: number
  /** Los textos de las acciones, tal como salen del módulo de contenido. */
  copy: { action: string; printAction: string; hint: string }
  /** El PDF estático de `public/`, generado por scripts/halloween-booklet/generate-pdf.mjs. */
  pdf: { href: string; filename: string }
  /** Enlace al producto bajo el botón, en el momento de mayor intención. */
  funnel: { href: string; label: string }
}) {
  const bookletRef = useRef<HTMLDivElement>(null)

  /** `method` distingue los dos caminos para poder ver cuál usa la gente. */
  function track(method: 'pdf' | 'print') {
    captureProductEvent(PRODUCT_EVENTS.GUIDE_BOOKLET_PRINTED, {
      guide_slug: GUIDE_SLUG,
      pages: pageCount,
      method,
    })
  }

  /**
   * El embudo se mide con el MISMO evento y la misma familia de `placement` que
   * los otros dos CTA del artículo (`guide-cta-link`): sin esto no se puede
   * responder la pregunta que motivó ponerlo aquí — si acercar el enlace al
   * botón mueve algo. La distancia («7.852 px → 913») mide la posición, no el
   * efecto.
   */
  function trackFunnel() {
    captureProductEvent(PRODUCT_EVENTS.GUIDE_CTA_CLICKED, {
      character_id: 'halloween',
      guide_slug: GUIDE_SLUG,
      locale: 'es',
      placement: 'booklet',
    })
  }

  function handlePrint() {
    if (!bookletRef.current) return
    // El evento va ANTES: `window.print()` bloquea en algunos navegadores hasta
    // que se cierra el diálogo, así que medir después registra la intención
    // tarde — y la pierde entera si el usuario cierra la pestaña.
    track('print')
    printElement(bookletRef.current)
  }

  return (
    <div className="space-y-6">
      <div className="rounded-3xl border border-white/15 bg-white/10 p-6 text-center print:hidden">
        <Button
          asChild
          className="font-christmas w-full rounded-full bg-orange-500 px-8 py-7 text-lg font-bold text-white shadow-lg transition-transform hover:scale-105 hover:bg-orange-400 sm:w-auto"
        >
          <a href={pdf.href} download={pdf.filename} onClick={() => track('pdf')}>
            <Download className="size-5" aria-hidden />
            {copy.action}
          </a>
        </Button>

        <p className="mx-auto mt-4 max-w-md text-sm text-white/75">{copy.hint}</p>

        <button
          type="button"
          onClick={handlePrint}
          className="mx-auto mt-3 flex items-center gap-2 text-sm text-white/70 underline underline-offset-4 hover:text-white"
        >
          <Printer className="size-4" aria-hidden />
          {copy.printAction}
        </button>

        <p className="mt-4 border-t border-white/15 pt-4 text-sm text-white/85">
          <Link
            href={funnel.href}
            onClick={trackFunnel}
            className="font-semibold text-orange-300 underline decoration-2 underline-offset-4 hover:text-orange-200"
          >
            {funnel.label} →
          </Link>
        </p>
      </div>

      {/* En pantalla el cuadernillo se lee en una columna estrecha; en papel esa
          anchura dejaría un tercio de cada hoja en blanco. */}
      <div ref={bookletRef} data-booklet className="mx-auto max-w-xl space-y-6 print:max-w-none">
        {children}
      </div>
    </div>
  )
}
